import type { TeamLogMessage } from "../types";

export type ViewMode = "log" | "chat";

export const SYSTEM_TAGS: ReadonlySet<string> = new Set([
  "pre-flight",
  "build-complete",
  "directive",
  "issue",
  "onboarding",
  "relay-system",
]);

const CHAT_OVERRIDE_TAG = "chat";

export function isConversational(msg: Pick<TeamLogMessage, "tags">): boolean {
  const tags = msg.tags;
  if (!tags || tags.length === 0) return true;
  if (tags.includes(CHAT_OVERRIDE_TAG)) return true;
  for (const tag of tags) {
    if (SYSTEM_TAGS.has(tag)) return false;
  }
  return true;
}

// S2-4 chat-window predicate per spec #1129. Distinct from `isConversational`
// (legacy permissive filter). `isChatMessage` is opinionated: anything with
// any system tag is hidden, even if also tagged `chat`. Inclusion requires
// an explicit signal — `chat` tag, human-lead author, or a question
// directed at the human lead.
//
// seq #22352 — humanLeadId used to be a hardcoded 'travis' constant
// (Sprint 2 placeholder), silently mismatching any workspace where the
// lead's author string isn't literally "travis" — the same class of bug
// already fixed elsewhere for exempt_authors/verifiedRole (seq #31987).
// Now a parameter with 'travis' as the default so every existing call site
// keeps its current behavior unless it passes a real value.
const DEFAULT_HUMAN_LEAD_ID = "travis";

export const CHAT_WINDOW_SYSTEM_TAGS: ReadonlySet<string> = new Set([
  "pre-flight",
  "build-complete",
  "directive",
  "onboarding",
  "relay-system",
  "nudge",
  "context-summary",
  "retiring",
  "retired-by-planner",
  "deployed",
  "sprint-open",
  "sprint-close",
]);

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function isChatMessage(
  msg: Pick<TeamLogMessage, "tags" | "author" | "content">,
  humanLeadId: string = DEFAULT_HUMAN_LEAD_ID,
): boolean {
  const tags = msg.tags ?? [];
  for (const t of tags) {
    if (CHAT_WINDOW_SYSTEM_TAGS.has(t)) return false;
  }
  if (tags.includes("chat")) return true;
  if (msg.author === humanLeadId) return true;
  if (tags.includes("question")) {
    const content = msg.content ?? "";
    const mentionRe = new RegExp(`(?:^|\\s)@${escapeRegExp(humanLeadId)}\\b`);
    if (mentionRe.test(content)) return true;
  }
  return false;
}

export function parseViewMode(search: string): ViewMode {
  if (!search) return "log";
  const trimmed = search.startsWith("?") ? search.slice(1) : search;
  for (const pair of trimmed.split("&")) {
    const [rawKey, rawValue = ""] = pair.split("=");
    if (rawKey === "view" && rawValue === "chat") return "chat";
  }
  return "log";
}
